import type { DetectionProfile } from './profile'

// Interrupteurs de règles pour la calibration sur le terrain (TEMPORAIRE, lot O7). Toutes actives par défaut : le
// moteur se comporte alors exactement selon le profil. Une règle coupée est neutralisée par une valeur de paramètre
// sans effet (seuils immenses, confirmation sur une seule position) ou, pour l'armement et l'ambiguïté, par un
// interrupteur lu par le moteur. Jamais transmis au serveur.

export const ruleIds = [
  'precision',
  'freshness',
  'jump',
  'captureConfirm',
  'arming',
  'ambiguity',
  'minDuration',
  'movement',
  'exitConfirm',
  'gap',
] as const
export type RuleId = (typeof ruleIds)[number]
export type EngineRules = Record<RuleId, boolean>

export const allRulesOn: EngineRules = Object.fromEntries(ruleIds.map((id) => [id, true])) as EngineRules

type NumericKey = {
  [K in keyof DetectionProfile]: DetectionProfile[K] extends number ? K : never
}[keyof DetectionProfile]

export interface RuleInfo {
  id: RuleId
  label: string
  /** Effet de la coupure, en clair. */
  whenOff: string
  /** Paramètres réglant cette règle. */
  parameters: NumericKey[]
}

/** Valeur « sans limite » : finie, pour survivre aux copies JSON de l'état du moteur. */
const unlimited = 1e9

export const ruleInfo: RuleInfo[] = [
  {
    id: 'precision',
    label: 'Précision GPS minimale',
    whenOff: 'toute position est acceptée, quelle que soit sa précision',
    parameters: ['maxAccuracyM'],
  },
  {
    id: 'freshness',
    label: 'Fraîcheur du relevé et perte de signal',
    whenOff: 'une vieille position est acceptée et le silence du GPS ne suspend plus la détection',
    parameters: ['maxFixAgeS'],
  },
  {
    id: 'jump',
    label: 'Saut de position incohérent',
    whenOff: 'aucune position n’est écartée pour vitesse impossible',
    parameters: ['maxSpeedMps'],
  },
  {
    id: 'captureConfirm',
    label: 'Confirmation de capture',
    whenOff: 'capture dès la première position dans le rayon de capture',
    parameters: ['captureConfirmCount', 'captureConfirmWindowS'],
  },
  {
    id: 'arming',
    label: 'Entrée exigée (désarmement au départ)',
    whenOff: 'une résidence déjà sous le véhicule peut être capturée sans en sortir',
    parameters: [],
  },
  {
    id: 'ambiguity',
    label: 'Choix d’adresse en cas d’ambiguïté',
    whenOff: 'le drapeau le plus proche est capturé, sans demander',
    parameters: [],
  },
  {
    id: 'minDuration',
    label: 'Durée minimale d’intervention',
    whenOff: 'une visite de toute durée est admissible',
    parameters: ['minInterventionS'],
  },
  {
    id: 'movement',
    label: 'Part minimale en mouvement',
    whenOff: 'un arrêt prolongé compte comme du travail',
    parameters: ['minMovingShare', 'movementWindowS', 'stationaryJitterM'],
  },
  {
    id: 'exitConfirm',
    label: 'Confirmation de sortie',
    whenOff: 'sortie dès la première position au-delà de la distance de sortie',
    parameters: ['exitConfirmCount', 'exitConfirmWindowS'],
  },
  {
    id: 'gap',
    label: 'Trou maximal crédité',
    whenOff: 'le temps entre deux positions est crédité même après un long trou',
    parameters: ['maxGapS'],
  },
]

/** Profil effectivement donné au moteur : les règles coupées sont neutralisées. */
export function applyRules(profile: DetectionProfile, rules: EngineRules): DetectionProfile {
  const effective = { ...profile }
  if (!rules.precision) effective.maxAccuracyM = unlimited
  if (!rules.freshness) effective.maxFixAgeS = unlimited
  if (!rules.jump) effective.maxSpeedMps = unlimited
  if (!rules.captureConfirm) {
    effective.captureConfirmCount = 1
    effective.captureConfirmWindowS = 0
  }
  if (!rules.minDuration) effective.minInterventionS = 0
  if (!rules.movement) effective.minMovingShare = 0
  if (!rules.exitConfirm) {
    effective.exitConfirmCount = 1
    effective.exitConfirmWindowS = 0
  }
  if (!rules.gap) effective.maxGapS = unlimited
  return effective
}
